import { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'components/DashboardNavbar';
import Footer from 'examples/Footer';

import DateFilterPresets from 'components/DateFilterPresets';
import EntityFilter from 'components/EntityFilter';
import MetricCard from 'components/MetricCard';
import usePersistedFilters from 'hooks/usePersistedFilters';

import { getStatistics, ggrForPeriod, getSlotsMetrics } from 'services/analytics';
import { exportDashboardToCsv } from 'utils/exportToCsv';

function SlotsDashboard() {
  const { filters, setDateRange, setEntityFilters } = usePersistedFilters();

  const [statistics, setStatistics] = useState(null);
  const [ggr, setGgr] = useState(null);
  const [slotsMetrics, setSlotsMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const clientNames = filters.clientNames || [];

      // Build filter params for ALL APIs - use first ID from arrays for single-select APIs
      const apiFilters = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        clientId: filters.clientIds?.[0] || undefined,
        operatorId: filters.operatorIds?.[0] || undefined,
        brandId: filters.brandIds?.[0] || undefined,
        gameCategory: 'slot'
      };

      const [statsRes, ggrRes, slotsRes] = await Promise.all([
        getStatistics(apiFilters),
        ggrForPeriod(apiFilters),
        getSlotsMetrics(apiFilters)
      ]);

      let ggrData = ggrRes?.data || [];

      if (clientNames.length > 0 && Array.isArray(ggrData)) {
        ggrData = ggrData.filter((g) => clientNames.includes(g.client));
      }

      setStatistics(statsRes?.data || {});
      setGgr(ggrData);
      setSlotsMetrics(slotsRes?.success ? slotsRes.data : null);
    } catch (err) {
      console.error('Error fetching Slots dashboard data:', err);
    }
    setLoading(false);
  }, [
    filters.startDate,
    filters.endDate,
    filters.clientNames,
    filters.clientIds,
    filters.operatorIds,
    filters.brandIds
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDateChange = (startDate, endDate, preset) => {
    setDateRange(startDate, endDate, preset);
  };

  const handleEntityFilterChange = (entityFilters) => {
    setEntityFilters(entityFilters);
  };

  const getGgrTotal = () => {
    if (!ggr || !Array.isArray(ggr) || ggr.length === 0) return '-';
    const total = ggr.reduce((sum, g) => sum + (parseFloat(g.ggr) || 0), 0);
    return total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getGgrBreakdown = () => {
    if (!ggr || !Array.isArray(ggr) || ggr.length === 0) return null;
    return [...ggr]
      .sort((a, b) => (parseFloat(b.ggr) || 0) - (parseFloat(a.ggr) || 0))
      .map((g) => ({
        label: g.client || g.clientName || 'Unknown',
        value: parseFloat(g.ggr).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      }));
  };

  const getBetsBreakdown = () => {
    if (!statistics?.betsForPeriodAmount) return null;
    return statistics.betsForPeriodAmount.map((b) => ({
      label: b.currency,
      value: parseFloat(b.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }));
  };

  // Slots metrics helpers
  const getSlotsGgr = () => {
    if (slotsMetrics?.ggr !== undefined) {
      return slotsMetrics.ggr.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return getGgrTotal();
  };

  const getSlotsNgr = () => {
    if (slotsMetrics?.ngr !== undefined) {
      return slotsMetrics.ngr.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return null;
  };

  const getSlotsTotalBets = () => {
    if (slotsMetrics?.totalBets) {
      return {
        count: slotsMetrics.totalBets.count.toLocaleString(),
        amount: slotsMetrics.totalBets.amount.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })
      };
    }
    return null;
  };

  const getSlotsTotalWins = () => {
    if (slotsMetrics?.totalWins) {
      return {
        count: slotsMetrics.totalWins.count.toLocaleString(),
        amount: slotsMetrics.totalWins.amount.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })
      };
    }
    return null;
  };

  const getSlotsRoundsPlayed = () => {
    if (slotsMetrics?.roundsPlayed !== undefined) {
      return slotsMetrics.roundsPlayed.toLocaleString();
    }
    return null;
  };

  const getSlotsAvgBetSize = () => {
    if (slotsMetrics?.averageBetSize !== undefined) {
      return slotsMetrics.averageBetSize.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
    return null;
  };

  const getSlotsUniquePlayers = () => {
    if (slotsMetrics?.uniquePlayers !== undefined) {
      return slotsMetrics.uniquePlayers.toLocaleString();
    }
    return null;
  };

  const getSlotsTotalSessions = () => {
    if (slotsMetrics?.totalSessions !== undefined) {
      return slotsMetrics.totalSessions.toLocaleString();
    }
    return null;
  };

  const getSlotsActualRtp = () => {
    if (slotsMetrics?.actualRtp !== undefined) {
      return slotsMetrics.actualRtp.toFixed(2) + '%';
    }
    return null;
  };

  const getSlotsTheoreticalRtp = () => {
    if (slotsMetrics?.theoreticalRtp !== undefined && slotsMetrics.theoreticalRtp !== null) {
      return slotsMetrics.theoreticalRtp.toFixed(2) + '%';
    }
    return null;
  };

  const getSlotsRtpVariance = () => {
    if (slotsMetrics?.rtpVariance !== undefined && slotsMetrics.rtpVariance !== null) {
      const variance = slotsMetrics.rtpVariance;
      const sign = variance >= 0 ? '+' : '';
      return sign + variance.toFixed(2) + '%';
    }
    return null;
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={{ xs: 1, md: 3 }}>
        <MDBox mb={3}>
          <Card>
            {/* Red accent line */}
            <MDBox
              sx={{
                height: '3px',
                background: 'linear-gradient(90deg, #e2001b, #ff4f69, transparent 80%)',
                borderRadius: '12px 12px 0 0'
              }}
            />
            <MDBox p={{ xs: 1.5, md: 3 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='center' mb={2}>
                <MDTypography variant='h5' fontWeight='bold'>
                  Slots Dashboard
                </MDTypography>
                <MDBox display='flex' gap={1}>
                  <MDButton
                    variant='outlined'
                    color='success'
                    size='small'
                    onClick={() => exportDashboardToCsv('slots-dashboard-export', { ggr, statistics, filters })}
                    disabled={loading}
                  >
                    <Icon sx={{ mr: 0.5 }}>download</Icon>
                    Export CSV
                  </MDButton>
                  <MDButton variant='outlined' color='info' size='small' onClick={fetchData}>
                    Refresh
                  </MDButton>
                </MDBox>
              </MDBox>

              <MDBox mb={3}>
                <MDTypography variant='button' color='text' fontWeight='regular' mb={1}>
                  Date Range
                </MDTypography>
                <DateFilterPresets
                  onDateChange={handleDateChange}
                  preset={filters.preset || 'today'}
                  startDate={filters.startDate}
                  endDate={filters.endDate}
                />
              </MDBox>

              <MDBox>
                <MDTypography variant='button' color='text' fontWeight='regular' mb={1}>
                  Entity Filters
                </MDTypography>
                <EntityFilter
                  onFilterChange={handleEntityFilterChange}
                  initialValues={{
                    clientIds: filters.clientIds,
                    operatorIds: filters.operatorIds,
                    brandIds: filters.brandIds
                  }}
                />
              </MDBox>
            </MDBox>
          </Card>
        </MDBox>

        <MDBox mb={3}>
          <MDTypography variant='h6' fontWeight='medium' mb={2}>
            Core Financial Metrics
          </MDTypography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title='GGR'
                value={getSlotsGgr()}
                subtitle='Gross Gaming Revenue'
                icon='attach_money'
                iconColor='success'
                loading={loading}
                breakdown={getGgrBreakdown()}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title='NGR'
                value={getSlotsNgr()}
                placeholder={!getSlotsNgr()}
                placeholderText='No data'
                subtitle='Net Gaming Revenue'
                icon='trending_up'
                iconColor='info'
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title='Total Bets'
                value={getSlotsTotalBets()?.count || statistics?.betsForPeriod?.toLocaleString() || '-'}
                subtitle={getSlotsTotalBets() ? `$${getSlotsTotalBets().amount} turnover` : 'Count'}
                icon='casino'
                iconColor='primary'
                loading={loading}
                breakdown={getBetsBreakdown()}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title='Total Wins'
                value={getSlotsTotalWins()?.count}
                placeholder={!getSlotsTotalWins()}
                placeholderText='No data'
                subtitle={getSlotsTotalWins() ? `$${getSlotsTotalWins().amount} payout` : 'Player Winnings'}
                icon='emoji_events'
                iconColor='warning'
                loading={loading}
              />
            </Grid>
          </Grid>
        </MDBox>

        <MDBox mb={3}>
          <MDTypography variant='h6' fontWeight='medium' mb={2}>
            Activity & Engagement Metrics
          </MDTypography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <MetricCard
                title='Rounds Played'
                value={getSlotsRoundsPlayed()}
                placeholder={!getSlotsRoundsPlayed()}
                placeholderText='No data'
                icon='sports_esports'
                iconColor='info'
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MetricCard
                title='Average Bet Size'
                value={getSlotsAvgBetSize() ? `$${getSlotsAvgBetSize()}` : null}
                placeholder={!getSlotsAvgBetSize()}
                placeholderText='No data'
                icon='payments'
                iconColor='secondary'
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MetricCard
                title='Unique Players'
                value={getSlotsUniquePlayers()}
                placeholder={!getSlotsUniquePlayers()}
                placeholderText='No data'
                icon='people'
                iconColor='info'
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MetricCard
                title='Total Sessions'
                value={getSlotsTotalSessions()}
                placeholder={!getSlotsTotalSessions()}
                placeholderText='No data'
                icon='event'
                iconColor='primary'
                loading={loading}
              />
            </Grid>
          </Grid>
        </MDBox>

        <MDBox mb={3}>
          <MDTypography variant='h6' fontWeight='medium' mb={2}>
            Slots-Specific Metrics (RTP)
          </MDTypography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} lg={4}>
              <MetricCard
                title='Actual RTP'
                value={getSlotsActualRtp()}
                placeholder={!getSlotsActualRtp()}
                placeholderText='No data'
                subtitle='Return to Player'
                icon='percent'
                iconColor='success'
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <MetricCard
                title='Theoretical RTP'
                value={getSlotsTheoreticalRtp()}
                placeholder={!getSlotsTheoreticalRtp()}
                placeholderText='No data'
                subtitle='Expected RTP'
                icon='analytics'
                iconColor='info'
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <MetricCard
                title='RTP Variance'
                value={getSlotsRtpVariance()}
                placeholder={!getSlotsRtpVariance()}
                placeholderText='No data'
                subtitle='Actual vs Theoretical'
                icon='compare_arrows'
                iconColor='warning'
                loading={loading}
              />
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default SlotsDashboard;
